import {
	type AthenaPostgresPool,
	createPostgresPool,
	postgresPoolDiagnostics,
} from "../../postgres/driver.ts";
import {
	type AthenaPostgresRuntime,
	connectPostgresPoolWithDeadline,
} from "../../postgres/owned-runtime.ts";
import { assertNodePostgresRuntime } from "../../postgres/runtime.ts";
import { AthenaAuthRuntimeError } from "./errors.ts";
import { currentAuthRequestTiming } from "./request-timing.ts";

export type AuthDatabaseTimeoutPhase =
	| "sql_acquire"
	| "sql_exec"
	| "transaction";

function isAuthJsDebugEnabled(): boolean {
	const value = (
		globalThis as { process?: { env?: Record<string, string | undefined> } }
	).process?.env?.ATHENA_JS_DEBUG;
	return value === "1" || value === "true";
}

function logAuthDatabaseTimeout(phase: AuthDatabaseTimeoutPhase): void {
	if (!isAuthJsDebugEnabled()) {
		return;
	}
	const timing = currentAuthRequestTiming();
	const snap = timing?.copy();
	console.warn("[athena-auth] ATHENA_AUTH_DATABASE_TIMEOUT", {
		phase,
		pool: {
			idle: snap?.sqlPoolIdle ?? 0,
			total: snap?.sqlPoolTotal ?? 0,
			waiting: snap?.sqlPoolWaiting ?? 0,
		},
		sqlAcquireMs: snap?.sqlAcquireMs ?? 0,
		sqlExecMs: snap?.sqlExecMs ?? 0,
		traceId: timing?.traceId() ?? null,
	});
}

export const ATHENA_AUTH_DATABASE_OPERATION_TIMEOUT_MS = 15_000;

export interface AthenaAuthQueryResult<T = Record<string, unknown>> {
	rowCount: number;
	rows: T[];
}

export interface AthenaAuthDatabase {
	close?(): Promise<void>;
	query<T = Record<string, unknown>>(
		text: string,
		values?: unknown[],
	): Promise<AthenaAuthQueryResult<T>>;
	transaction<T>(fn: (db: AthenaAuthDatabase) => Promise<T>): Promise<T>;
}

/**
 * Normalize any driver-shaped query result into the Auth database contract.
 * Never let callers hit `undefined.rows` / `undefined.length`.
 */
export function assertQueryResult<T = Record<string, unknown>>(
	value: unknown,
	context: string,
): AthenaAuthQueryResult<T> {
	if (Array.isArray(value)) {
		return {
			rowCount: value.length,
			rows: value as T[],
		};
	}

	if (value === null || value === undefined || typeof value !== "object") {
		throw new AthenaAuthRuntimeError(
			500,
			[
				"ATHENA_AUTH_DATABASE_RESULT_INVALID",
				"",
				"Embedded Auth database adapter returned an unexpected query result.",
				"",
				`Context: ${context}`,
				"",
				"Run with:",
				"  ATHENA_JS_DEBUG=1 athena-js migrate",
			].join("\n"),
			{ code: "ATHENA_AUTH_DATABASE_RESULT_INVALID" },
		);
	}

	const record = value as Record<string, unknown>;
	const rows = record.rows;
	if (!Array.isArray(rows)) {
		throw new AthenaAuthRuntimeError(
			500,
			[
				"ATHENA_AUTH_DATABASE_RESULT_INVALID",
				"",
				"Embedded Auth database adapter returned an invalid result while",
				`${context}: expected result.rows to be an array.`,
				"",
				"Run with:",
				"  ATHENA_JS_DEBUG=1 athena-js migrate",
			].join("\n"),
			{ code: "ATHENA_AUTH_DATABASE_RESULT_INVALID" },
		);
	}

	const rawCount = record.rowCount;
	const rowCount =
		typeof rawCount === "number" && Number.isFinite(rawCount)
			? rawCount
			: rows.length;

	return {
		rowCount,
		rows: rows as T[],
	};
}

function isDatabaseTimeoutError(error: unknown): boolean {
	if (
		error instanceof AthenaAuthRuntimeError &&
		error.code === "ATHENA_AUTH_DATABASE_TIMEOUT"
	) {
		return true;
	}
	if (!error || typeof error !== "object") {
		return false;
	}
	const record = error as { code?: unknown; message?: unknown };
	if (record.code === "ETIMEDOUT" || record.code === "57014") {
		return true;
	}
	return (
		typeof record.message === "string" &&
		/timeout exceeded|query_timeout|statement timeout|connection timeout/i.test(
			record.message,
		)
	);
}

export function authDatabaseTimeoutError(
	cause?: unknown,
): AthenaAuthRuntimeError {
	return new AthenaAuthRuntimeError(
		504,
		[
			"ATHENA_AUTH_DATABASE_TIMEOUT",
			"",
			"Embedded Auth could not complete a database operation before the timeout.",
		].join("\n"),
		{ cause, code: "ATHENA_AUTH_DATABASE_TIMEOUT" },
	);
}

async function withAuthDatabaseTimeout<T>(
	operation: () => Promise<T>,
	timeoutMs: number,
	phase: AuthDatabaseTimeoutPhase,
): Promise<T> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	try {
		return await Promise.race([
			operation().then(
				(value) => value,
				(error: unknown) => {
					if (isDatabaseTimeoutError(error)) {
						logAuthDatabaseTimeout(phase);
						throw authDatabaseTimeoutError(error);
					}
					throw error;
				},
			),
			new Promise<never>((_, reject) => {
				timer = setTimeout(() => {
					logAuthDatabaseTimeout(phase);
					reject(authDatabaseTimeoutError());
				}, timeoutMs);
			}),
		]);
	} finally {
		if (timer !== undefined) {
			clearTimeout(timer);
		}
	}
}

function recordSqlTiming(idleCount: number, elapsedMs: number): void {
	const timing = currentAuthRequestTiming();
	if (!timing) {
		return;
	}
	if (idleCount <= 0) {
		timing.addSqlAcquire(elapsedMs);
	} else {
		timing.addSqlExec(elapsedMs);
	}
	timing.addSqlCount(1);
}

function normalizeDriverResult<T>(
	result: unknown,
	context: string,
): AthenaAuthQueryResult<T> {
	return assertQueryResult<T>(result, context);
}

class PoolDatabase implements AthenaAuthDatabase {
	constructor(
		private readonly pool: AthenaPostgresPool,
		private readonly ownership: "owned" | "borrowed" = "owned",
		private readonly operationTimeoutMs = ATHENA_AUTH_DATABASE_OPERATION_TIMEOUT_MS,
	) { }

	close = async (): Promise<void> => {
		if (this.ownership === "borrowed") {
			return;
		}
		await this.pool.end();
	};

	query = async <T = Record<string, unknown>>(
		text: string,
		values?: unknown[],
	): Promise<AthenaAuthQueryResult<T>> => {
		const stats = postgresPoolDiagnostics(this.pool);
		currentAuthRequestTiming()?.setSqlPool(stats);
		return withAuthDatabaseTimeout(
			async () => {
				const started = performance.now();
				const result = await this.pool.query<T & Record<string, unknown>>(
					text,
					values,
				);
				recordSqlTiming(stats.idleCount, performance.now() - started);
				return normalizeDriverResult<T>(result, "pool.query");
			},
			this.operationTimeoutMs,
			stats.idleCount <= 0 ? "sql_acquire" : "sql_exec",
		);
	};

	transaction = async <T>(
		fn: (db: AthenaAuthDatabase) => Promise<T>,
	): Promise<T> => {
		return withAuthDatabaseTimeout(
			async () => {
				const acquireStarted = performance.now();
				const client = await connectPostgresPoolWithDeadline(
					this.pool,
					this.operationTimeoutMs,
				);
				currentAuthRequestTiming()?.addSqlAcquire(
					performance.now() - acquireStarted,
				);
				try {
					await client.query("BEGIN");
					const scoped: AthenaAuthDatabase = {
						query: async <TRow = Record<string, unknown>>(
							text: string,
							values?: unknown[],
						) => {
							const execStarted = performance.now();
							const result = await client.query(text, values);
							const timing = currentAuthRequestTiming();
							if (timing) {
								timing.addSqlExec(performance.now() - execStarted);
								timing.addSqlCount(1);
							}
							return normalizeDriverResult<TRow>(
								result,
								"transaction.query",
							);
						},
						transaction: (inner) => inner(scoped),
					};
					const value = await fn(scoped);
					await client.query("COMMIT");
					return value;
				} catch (error) {
					try {
						await client.query("ROLLBACK");
					} catch {
						// ignore rollback failure
					}
					throw error;
				} finally {
					client.release();
				}
			},
			this.operationTimeoutMs,
			"transaction",
		);
	};
}

export async function createPostgresAuthDatabase(
	connectionString: string,
): Promise<AthenaAuthDatabase> {
	assertNodePostgresRuntime();
	const pool = await createPostgresPool(connectionString, {
		max: 20,
		min: 4,
	});
	return new PoolDatabase(pool);
}

export function createAuthDatabaseFromPool(
	pool: AthenaPostgresPool,
): AthenaAuthDatabase {
	return new PoolDatabase(pool, "borrowed");
}

export const createPostgresAuthDatabaseFromPool = createAuthDatabaseFromPool;

export function createAuthDatabaseFromRuntime(
	runtime: Pick<AthenaPostgresRuntime, "inspectPool" | "query" | "transaction">,
	options?: { operationTimeoutMs?: number },
): AthenaAuthDatabase {
	const timeoutMs =
		options?.operationTimeoutMs ?? ATHENA_AUTH_DATABASE_OPERATION_TIMEOUT_MS;

	const adapter: AthenaAuthDatabase = {
		async close() { },
		async query(text, values) {
			const stats = await runtime.inspectPool();
			currentAuthRequestTiming()?.setSqlPool(stats);
			return withAuthDatabaseTimeout(
				async () => {
					const started = performance.now();
					const result = await runtime.query(text, values);
					recordSqlTiming(stats.idleCount, performance.now() - started);
					return assertQueryResult(result, "runtime.query");
				},
				timeoutMs,
				stats.idleCount <= 0 ? "sql_acquire" : "sql_exec",
			);
		},
		async transaction(fn) {
			return withAuthDatabaseTimeout(
				() =>
					runtime.transaction(
						async (tx) => fn(createAuthDatabaseFromRuntime(tx, options)),
						{ deadlineMs: timeoutMs },
					),
				timeoutMs,
				"transaction",
			);
		},
	};
	return adapter;
}
